import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  Building2,
  Calculator,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Info,
  Layers,
  RefreshCcw,
  Users,
  X,
} from 'lucide-react';

import { Alert, AlertDescription } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent } from '@/ui/base/card';
import { Label } from '@/ui/base/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { Switch } from '@/ui/base/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/ui/base/table';
import { EmptyState } from '@/ui/EmptyState';
import { MoneyDisplay } from '@/ui/MoneyDisplay';
import { PageHeader } from '@/ui/PageHeader';
import { formatarDinheiro } from '@/nucleo/dinheiro';

import { useCenarioDeTela } from '../_dev/cenarioDeTela';
import { EstadoSimulado } from '../_dev/EstadoSimulado';
import { AprovarLoteAtesteModal } from './AprovarLoteAtesteModal';
import { CredenciadosTable } from './_tabela/CredenciadosTable';
import { formatarCompetencia } from './_tabela/statusAteste';
import { DevolverMedicaoModal } from './DevolverMedicaoModal';
import { FIXTURE_ATESTE } from './dados';
import type { CredenciadoAteste, DreAteste } from './tipos';

/**
 * `/operacao/financeiro/ateste-medicao` — cálculo e ateste da remuneração de
 * cada credenciado a partir dos apontamentos diários e da tabela de preços
 * vigente, com detalhamento em 4 níveis: DRE → credenciado → OS → dia.
 *
 * Dado simulado por decisão de produto (ver `../_dev/cenarioDeTela.ts`): a
 * API do módulo financeiro ainda não existe. Recalcular, aprovar em lote e
 * devolver atualizam só o estado local desta página — trocar por `dados.ts`
 * e mutações reais quando o módulo nascer na API.
 *
 * Simplificações em relação ao protótipo portado (ver relatório da porta):
 * o cálculo de desvio (`avaliarDesvio`/baselines históricos) vem pré-computado
 * na fixture, já que não há histórico real para comparar; os níveis de OS e
 * dia vêm todos já carregados na mesma fixture, sem busca lazy por nível; e o
 * "Ver OS" do protótipo foi removido por não existir tela de detalhe de OS
 * navegável nesta fase (regra do projeto: nenhuma rota órfã).
 */

const fixture = FIXTURE_ATESTE;

interface AvisoAcao {
  tom: 'sucesso' | 'info' | 'erro';
  mensagem: string;
}

interface TotaisPorTipo {
  valorPF: number;
  valorPJ: number;
  valorCoop: number;
  credsPF: number;
  credsPJ: number;
  credsCoop: number;
}

function agregarPorTipo(credenciados: CredenciadoAteste[]): TotaisPorTipo {
  const totais: TotaisPorTipo = {
    valorPF: 0,
    valorPJ: 0,
    valorCoop: 0,
    credsPF: 0,
    credsPJ: 0,
    credsCoop: 0,
  };
  for (const c of credenciados) {
    if (c.tipo === 'PF') {
      totais.valorPF += c.valorTotal;
      totais.credsPF += 1;
    } else if (c.tipo === 'PJ') {
      totais.valorPJ += c.valorTotal;
      totais.credsPJ += 1;
    } else {
      totais.valorCoop += c.valorTotal;
      totais.credsCoop += 1;
    }
  }
  return totais;
}

function gerarOpcoesMes(competencia: string): { value: string; label: string }[] {
  const partes = competencia.split('-').map(Number);
  const ano = partes[0] ?? new Date().getFullYear();
  const mes = partes[1] ?? 1;
  const opcoes: { value: string; label: string }[] = [];
  for (let i = 0; i < 12; i++) {
    const data = new Date(ano, mes - 1 - i, 1);
    const iso = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}-01`;
    opcoes.push({ value: iso, label: formatarCompetencia(iso) });
  }
  return opcoes;
}

const MENSAGEM_DEVOLUCAO_SEM_ALVO = 'Não foi possível identificar a medição a devolver.';

export function AtesteDeMedicaoPage() {
  const cenario = useCenarioDeTela();

  const [mes, setMes] = useState(fixture.competencia);
  const [dresState, setDresState] = useState<DreAteste[]>(() => fixture.dres);
  const [dreSelecionada, setDreSelecionada] = useState<string | null>(null);
  const [apenasDesvio, setApenasDesvio] = useState(false);
  const [calculandoIds, setCalculandoIds] = useState<Set<string>>(new Set());
  const [recalculandoTudo, setRecalculandoTudo] = useState(false);
  const [openAprovarLote, setOpenAprovarLote] = useState(false);
  const [devolverAlvo, setDevolverAlvo] = useState<{ credenciadoId: string; nome: string } | null>(
    null,
  );
  const [aviso, setAviso] = useState<AvisoAcao | null>(null);

  const opcoesMes = useMemo(() => gerarOpcoesMes(fixture.competencia), []);

  const dresVisiveis = useMemo(
    () => (cenario === 'sucesso' && mes === fixture.competencia ? dresState : []),
    [cenario, mes, dresState],
  );

  // ─── Auto-cálculo ao abrir a competência: roda uma vez, para medições sem
  // `calculadoEm` que já têm apontamento lançado. ──────────────────────────
  const autoRanRef = useRef<string | null>(null);
  useEffect(() => {
    if (cenario !== 'sucesso' || mes !== fixture.competencia) return;
    if (autoRanRef.current === mes) return;
    autoRanRef.current = mes;

    const pendentes = dresState
      .flatMap((d) => d.credenciados)
      .filter((c) => c.medicaoId && c.qtdApontamentos > 0 && !c.calculadoEm);
    if (pendentes.length === 0) return;

    const ids = pendentes.map((c) => c.credenciadoId);
    setCalculandoIds((atual) => new Set([...atual, ...ids]));
    setAviso({
      tom: 'info',
      mensagem: `Calculando competência: ${ids.length} medição(ões) sem cálculo. Rodando em segundo plano...`,
    });
    const temporizador = window.setTimeout(() => {
      aplicarPatch(new Set(ids), (c) => ({
        ...c,
        calculadoEm: new Date().toISOString(),
        statusMedicao: c.statusMedicao === 'RASCUNHO' ? 'PROCESSADA' : c.statusMedicao,
      }));
      setCalculandoIds((atual) => {
        const proximo = new Set(atual);
        ids.forEach((id) => proximo.delete(id));
        return proximo;
      });
      setAviso({
        tom: 'sucesso',
        mensagem: `Cálculo concluído: ${ids.length} medição(ões) calculada(s).`,
      });
    }, 900);
    return () => window.clearTimeout(temporizador);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cenario, mes]);

  // Aviso desaparece sozinho depois de um tempo, sem tirar quem já leu.
  useEffect(() => {
    if (!aviso) return;
    const temporizador = window.setTimeout(() => setAviso(null), 7000);
    return () => window.clearTimeout(temporizador);
  }, [aviso]);

  function aplicarPatch(ids: Set<string>, patch: (c: CredenciadoAteste) => CredenciadoAteste) {
    setDresState((atual) =>
      atual.map((d) => ({
        ...d,
        credenciados: d.credenciados.map((c) => (ids.has(c.credenciadoId) ? patch(c) : c)),
      })),
    );
  }

  const todosCredenciados = useMemo(
    () => dresVisiveis.flatMap((d) => d.credenciados),
    [dresVisiveis],
  );

  const totaisMes = useMemo(() => {
    const porTipo = agregarPorTipo(todosCredenciados);
    return {
      ...porTipo,
      dres: dresVisiveis.length,
      creds: todosCredenciados.length,
      medicoes: todosCredenciados.filter((c) => c.medicaoId).length,
      calculadas: todosCredenciados.filter((c) => c.calculadoEm).length,
      valor: todosCredenciados.reduce((acc, c) => acc + c.valorTotal, 0),
    };
  }, [dresVisiveis, todosCredenciados]);

  const candidatasLote = useMemo(
    () =>
      todosCredenciados
        .filter((c) => c.medicaoId && c.statusMedicao === 'PROCESSADA')
        .map((c) => ({
          medicaoId: c.medicaoId,
          credenciadoId: c.credenciadoId,
          nome: c.nome,
          tipo: c.tipo,
          valorTotal: c.valorTotal,
          desvio: c.desvio,
        })),
    [todosCredenciados],
  );

  const desviosCount = useMemo(
    () => todosCredenciados.filter((c) => c.desvio?.hasDesvio).length,
    [todosCredenciados],
  );

  const alertaVisivel = dresVisiveis.length > 0 && fixture.alertaTabelaAlterada;

  function handleRecalcular(credenciadoId: string) {
    const credenciado = todosCredenciados.find((c) => c.credenciadoId === credenciadoId);
    if (!credenciado) return;
    setCalculandoIds((atual) => new Set(atual).add(credenciadoId));
    window.setTimeout(() => {
      aplicarPatch(new Set([credenciadoId]), (c) => ({
        ...c,
        calculadoEm: new Date().toISOString(),
        statusMedicao: c.statusMedicao === 'RASCUNHO' ? 'PROCESSADA' : c.statusMedicao,
      }));
      setCalculandoIds((atual) => {
        const proximo = new Set(atual);
        proximo.delete(credenciadoId);
        return proximo;
      });
      setAviso({
        tom: 'sucesso',
        mensagem: `Cálculo concluído para ${credenciado.nome}. Total: ${formatarDinheiro(credenciado.valorTotal)}.`,
      });
    }, 700);
  }

  function handleRecalcularTudo() {
    const elegiveis = todosCredenciados.filter((c) => c.medicaoId && c.qtdApontamentos > 0);
    if (elegiveis.length === 0) {
      setAviso({ tom: 'info', mensagem: 'Nada para recalcular neste mês.' });
      return;
    }
    const ids = elegiveis.map((c) => c.credenciadoId);
    setRecalculandoTudo(true);
    setCalculandoIds((atual) => new Set([...atual, ...ids]));
    setAviso({ tom: 'info', mensagem: `Recalculando ${ids.length} medição(ões)...` });
    window.setTimeout(() => {
      aplicarPatch(new Set(ids), (c) => ({
        ...c,
        calculadoEm: new Date().toISOString(),
        statusMedicao: c.statusMedicao === 'RASCUNHO' ? 'PROCESSADA' : c.statusMedicao,
      }));
      setCalculandoIds((atual) => {
        const proximo = new Set(atual);
        ids.forEach((id) => proximo.delete(id));
        return proximo;
      });
      setRecalculandoTudo(false);
      setAviso({ tom: 'sucesso', mensagem: `${ids.length} medição(ões) recalculada(s).` });
    }, 1100);
  }

  function handleAprovarLote(medicaoIds: string[]) {
    const idsCredenciados = new Set(
      todosCredenciados.filter((c) => medicaoIds.includes(c.medicaoId)).map((c) => c.credenciadoId),
    );
    aplicarPatch(idsCredenciados, (c) => ({ ...c, statusMedicao: 'APROVADA' }));
    setOpenAprovarLote(false);
    setAviso({
      tom: 'sucesso',
      mensagem: `${medicaoIds.length} medição(ões) movida(s) para Aprovada.`,
    });
  }

  function handleDevolver(motivo: string) {
    if (!devolverAlvo) {
      setAviso({ tom: 'erro', mensagem: MENSAGEM_DEVOLUCAO_SEM_ALVO });
      return;
    }
    aplicarPatch(new Set([devolverAlvo.credenciadoId]), (c) => ({
      ...c,
      statusMedicao: 'DEVOLVIDA',
      motivoDevolucao: motivo,
    }));
    setAviso({
      tom: 'sucesso',
      mensagem:
        'Medição devolvida. Item travado para revisão; o restante do lote segue normalmente.',
    });
    setDevolverAlvo(null);
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Ateste de Medição — Simulador"
        description="Recalcule a remuneração de cada credenciado a partir dos apontamentos diários e da tabela vigente. Confira a composição (valor fixo, per capita, KM, atividade complementar, retenções) e compare com a medição oficial antes do pagamento."
      />

      {aviso && (
        <div role="status" aria-live="polite">
          <Alert
            className={
              aviso.tom === 'sucesso'
                ? 'border-success/50 bg-success/10 text-success'
                : aviso.tom === 'erro'
                  ? 'border-destructive/50 bg-destructive/10 text-destructive'
                  : 'border-info/50 bg-info/10 text-info'
            }
          >
            {aviso.tom === 'sucesso' ? (
              <CheckCircle2 className="text-success" aria-hidden />
            ) : aviso.tom === 'erro' ? (
              <AlertTriangle className="text-destructive" aria-hidden />
            ) : (
              <Info className="text-info" aria-hidden />
            )}
            <AlertDescription
              className={
                aviso.tom === 'sucesso'
                  ? 'text-success'
                  : aviso.tom === 'erro'
                    ? 'text-destructive'
                    : 'text-info'
              }
            >
              <div className="flex items-start justify-between gap-3">
                <span>{aviso.mensagem}</span>
                <button
                  type="button"
                  onClick={() => setAviso(null)}
                  aria-label="Fechar aviso"
                  className="shrink-0 rounded-sm text-current opacity-70 hover:opacity-100 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      )}

      {/* Filtro de mês + ações globais */}
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <div className="min-w-[220px] space-y-1">
            <Label htmlFor="ateste-mes">Mês referência</Label>
            <Select
              value={mes}
              onValueChange={(valor) => {
                setMes(valor);
                setDreSelecionada(null);
              }}
            >
              <SelectTrigger id="ateste-mes">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {opcoesMes.map((opcao) => (
                  <SelectItem key={opcao.value} value={opcao.value}>
                    {opcao.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleRecalcularTudo}
              disabled={cenario !== 'sucesso' || recalculandoTudo || dresVisiveis.length === 0}
            >
              {recalculandoTudo ? (
                <RefreshCcw className="h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <RefreshCcw className="h-3.5 w-3.5" aria-hidden />
              )}
              Recalcular tudo
            </Button>
            <Button
              size="sm"
              onClick={() => setOpenAprovarLote(true)}
              disabled={cenario !== 'sucesso' || candidatasLote.length === 0}
            >
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
              Aprovar lote ({candidatasLote.length})
            </Button>
          </div>

          {cenario === 'sucesso' && dresVisiveis.length > 0 && (
            <div className="ml-auto flex flex-wrap gap-4 text-xs">
              <div className="flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                <span className="text-muted-foreground">DREs:</span>
                <span className="font-mono font-medium">{totaisMes.dres}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                <span className="text-muted-foreground">Credenciados:</span>
                <span className="font-mono font-medium">{totaisMes.creds}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Layers className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                <span className="text-muted-foreground">Medições:</span>
                <span className="font-mono font-medium">
                  {totaisMes.calculadas}/{totaisMes.medicoes} calculadas
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                <span className="text-muted-foreground">Total mês:</span>
                <MoneyDisplay value={totaisMes.valor} className="font-semibold text-primary" />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <EstadoSimulado cenario={cenario} descricao="o ateste de medição" colunasDoEsqueleto={9}>
        {dresVisiveis.length === 0 ? (
          <Card>
            <CardContent className="p-0">
              <EmptyState
                icon={Calculator}
                title="Nenhuma medição neste mês"
                description="As medições aparecem aqui depois que a DRE lança o apontamento diário e a competência é calculada."
              />
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {/* Totais por tipo de pessoa */}
            <Card>
              <CardContent className="p-4">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Totais por tipo de pessoa
                  </span>
                  <div className="flex items-center gap-2">
                    <Label
                      htmlFor="apenas-desvio"
                      className="cursor-pointer normal-case tracking-normal"
                    >
                      Apenas com desvio
                      {desviosCount > 0 && (
                        <Badge
                          variant="outline"
                          className="ml-1.5 border-warning text-[10px] text-warning"
                        >
                          {desviosCount}
                        </Badge>
                      )}
                    </Label>
                    <Switch
                      id="apenas-desvio"
                      checked={apenasDesvio}
                      onCheckedChange={setApenasDesvio}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="rounded-lg border border-border p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">Pessoa Física</span>
                      <Badge variant="outline" className="text-[10px]">
                        {totaisMes.credsPF} cred.
                      </Badge>
                    </div>
                    <MoneyDisplay
                      value={totaisMes.valorPF}
                      className="mt-1 block text-base font-semibold text-primary"
                    />
                  </div>
                  <div className="rounded-lg border border-border p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">Pessoa Jurídica</span>
                      <Badge variant="secondary" className="text-[10px]">
                        {totaisMes.credsPJ} cred.
                      </Badge>
                    </div>
                    <MoneyDisplay
                      value={totaisMes.valorPJ}
                      className="mt-1 block text-base font-semibold text-primary"
                    />
                  </div>
                  <div className="rounded-lg border border-border p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">Cooperativa</span>
                      <Badge className="text-[10px]">{totaisMes.credsCoop} cred.</Badge>
                    </div>
                    <MoneyDisplay
                      value={totaisMes.valorCoop}
                      className="mt-1 block text-base font-semibold text-primary"
                    />
                  </div>
                </div>
                <p className="mt-2 text-[11px] text-muted-foreground">
                  Limites de desvio: <strong>{fixture.config.desvioValorPct}%</strong> valor ·{' '}
                  <strong>{fixture.config.desvioAlunosPct}%</strong> alunos · janela histórica:{' '}
                  <strong>{fixture.config.baselineMeses} meses</strong>
                </p>
              </CardContent>
            </Card>

            {alertaVisivel && (
              <Alert className="border-warning/40 bg-warning/10">
                <AlertTriangle className="h-4 w-4 text-warning" aria-hidden />
                <AlertDescription className="text-xs">
                  <strong className="text-sm text-foreground">
                    Tabela de preços alterada dentro do mês.
                  </strong>
                  <br />
                  Há mais de uma vigência de tabela ativa neste mês. Cálculos feitos antes da
                  mudança podem estar desatualizados — considere recalcular as medições afetadas.
                </AlertDescription>
              </Alert>
            )}

            {/* Nível 1: DREs */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table aria-label="Ateste de medição por DRE">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10" />
                        <TableHead>DRE</TableHead>
                        <TableHead className="text-right">Credenciados</TableHead>
                        <TableHead className="text-right">Medições</TableHead>
                        <TableHead className="text-right">Calculadas</TableHead>
                        <TableHead className="text-right">PF</TableHead>
                        <TableHead className="text-right">PJ</TableHead>
                        <TableHead className="text-right">COOP</TableHead>
                        <TableHead className="text-right">Valor consolidado</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dresVisiveis.map((dre) => {
                        const totaisDre = agregarPorTipo(dre.credenciados);
                        const valorTotalDre = dre.credenciados.reduce(
                          (acc, c) => acc + c.valorTotal,
                          0,
                        );
                        const aberta = dreSelecionada === dre.dreId;
                        return (
                          <Fragment key={dre.dreId}>
                            <TableRow
                              className="cursor-pointer hover:bg-muted/50"
                              onClick={() => setDreSelecionada(aberta ? null : dre.dreId)}
                            >
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  aria-label="Expandir DRE"
                                >
                                  {aberta ? (
                                    <ChevronDown className="h-4 w-4" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4" />
                                  )}
                                </Button>
                              </TableCell>
                              <TableCell className="font-medium">
                                {dre.dreCodigo ? `${dre.dreCodigo} — ` : ''}
                                {dre.dreNome}
                              </TableCell>
                              <TableCell className="text-right">
                                {dre.credenciados.length}
                              </TableCell>
                              <TableCell className="text-right">
                                {dre.credenciados.filter((c) => c.medicaoId).length}
                              </TableCell>
                              <TableCell className="text-right">
                                {dre.credenciados.filter((c) => c.calculadoEm).length}
                              </TableCell>
                              <TableCell className="text-right font-mono text-xs">
                                {formatarDinheiro(totaisDre.valorPF)}
                              </TableCell>
                              <TableCell className="text-right font-mono text-xs">
                                {formatarDinheiro(totaisDre.valorPJ)}
                              </TableCell>
                              <TableCell className="text-right font-mono text-xs">
                                {formatarDinheiro(totaisDre.valorCoop)}
                              </TableCell>
                              <TableCell className="text-right font-mono font-semibold">
                                {formatarDinheiro(valorTotalDre)}
                              </TableCell>
                            </TableRow>
                            {aberta && (
                              <TableRow>
                                <TableCell colSpan={9} className="border-0 p-0">
                                  <CredenciadosTable
                                    credenciados={dre.credenciados}
                                    apenasDesvio={apenasDesvio}
                                    cfg={fixture.config}
                                    calculandoIds={calculandoIds}
                                    onRecalcular={handleRecalcular}
                                    onDevolver={(credenciadoId, nome) =>
                                      setDevolverAlvo({ credenciadoId, nome })
                                    }
                                  />
                                </TableCell>
                              </TableRow>
                            )}
                          </Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </EstadoSimulado>

      <AprovarLoteAtesteModal
        open={openAprovarLote}
        onOpenChange={setOpenAprovarLote}
        candidatas={candidatasLote}
        onConfirmar={handleAprovarLote}
      />

      <DevolverMedicaoModal
        open={!!devolverAlvo}
        onOpenChange={(aberto) => {
          if (!aberto) setDevolverAlvo(null);
        }}
        credenciadoNome={devolverAlvo?.nome ?? null}
        onConfirmar={handleDevolver}
      />
    </div>
  );
}

export default AtesteDeMedicaoPage;
